import type { ReactNode } from 'react';

import { cn } from '@/shared/lib/cn';
import { LanguageSelector } from '@/widgets/language-selector/language-selector';

export interface NotFoundStateProps {
  title: string;
  description: string;
  hint?: string;
  /** Status label above the illustration. Omit to hide. */
  code?: string;
  actions?: ReactNode;
  className?: string;
}

function NotFoundIllustration() {
  return (
    <svg
      viewBox="0 0 160 120"
      className="h-28 w-auto text-primary"
      aria-hidden
    >
      <rect
        x="18"
        y="28"
        width="124"
        height="72"
        rx="16"
        className="fill-primary/10"
      />
      <circle cx="80" cy="64" r="26" className="fill-card stroke-primary/30" strokeWidth="3" />
      <circle cx="80" cy="64" r="12" className="fill-primary/15" />
      <path
        d="M98 82 L118 102"
        className="stroke-primary"
        strokeWidth="6"
        strokeLinecap="round"
      />
      <circle cx="42" cy="40" r="4" className="fill-primary/40" />
      <circle cx="124" cy="38" r="3" className="fill-primary/30" />
    </svg>
  );
}

export function NotFoundState({
  title,
  description,
  hint,
  code,
  actions,
  className,
}: NotFoundStateProps) {
  return (
    <div
      className={cn(
        'relative flex min-h-screen flex-col items-center justify-center bg-content-bg px-4 py-16',
        className,
      )}
    >
      <div className="absolute left-3 top-3 z-10 sm:left-6 sm:top-6">
        <LanguageSelector />
      </div>

      <div className="w-full max-w-md rounded-2xl border border-border bg-card px-6 py-10 text-center shadow-sm sm:px-10">
        {code ? (
          <p className="text-sm font-semibold tracking-[0.2em] text-primary/80">
            {code}
          </p>
        ) : null}

        <div className="mt-4 flex justify-center">
          <NotFoundIllustration />
        </div>

        <h1 className="mt-5 text-2xl font-bold tracking-tight text-body">
          {title}
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-secondary">
          {description}
        </p>
        {hint ? (
          <p className="mt-2 text-sm leading-relaxed text-secondary/80">
            {hint}
          </p>
        ) : null}

        {actions ? (
          <div className="mt-8 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center">
            {actions}
          </div>
        ) : null}
      </div>
    </div>
  );
}
